import type { AppConfig, AuthTokens, CharacterView, LedgerEntryView, LegalDoc, PreregRequest, MeResponse, PublicShareView, RedirectUrl, ShareEvent, WalletView, WebCatalog, WebCurrency } from '@lc/contracts';

export type LegalPage = 'privacy' | 'terms' | 'support';

/** The API base (…/api/v1). The local API by default. */
export const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api/v1').replace(/\/$/, '');

const SESSION_KEY = 'inked.web.session';
const DEVICE_KEY = 'inked.web.device';

export interface Session {
  accessToken: string;
  refreshToken: string;
  userId: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

const read = <T,>(key: string): T | null => {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
};
const write = (key: string, v: unknown) => {
  try {
    if (v === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(v));
  } catch {
    // Private mode: the session lasts this tab only.
  }
};

let session: Session | null = read<Session>(SESSION_KEY);
const listeners = new Set<() => void>();
export const getSession = () => session;
export const onSession = (f: () => void) => (listeners.add(f), () => void listeners.delete(f));
function setSession(s: Session | null) {
  session = s;
  write(SESSION_KEY, s);
  listeners.forEach((f) => f());
}

/** This browser, as a device the API knows (the same shape the app sends). */
function device() {
  let id = read<string>(DEVICE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    write(DEVICE_KEY, id);
  }
  return { deviceId: id, platform: 'web' as const, appVersion: '1.0.0' };
}

let lang = 'zh-HK';
export const setApiLanguage = (l: string) => void (lang = l);

async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown, retry = true): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      'accept-language': lang,
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(session ? { authorization: `Bearer ${session.accessToken}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && session && retry && (await refresh())) return call<T>(method, path, body, false);
  if (res.status === 204) return undefined as T;
  const json = (await res.json().catch(() => ({}))) as T & { error?: { code?: string; message?: string } };
  if (!res.ok) throw new ApiError(res.status, json.error?.code ?? 'error', json.error?.message ?? `HTTP ${res.status}`);
  return json;
}

/** A new access token from the refresh token; signs out when that's no longer valid. */
async function refresh(): Promise<boolean> {
  if (!session) return false;
  try {
    const t = await call<AuthTokens>('POST', '/auth/refresh', { refreshToken: session.refreshToken }, false);
    setSession({ accessToken: t.accessToken, refreshToken: t.refreshToken, userId: t.userId });
    return true;
  } catch {
    setSession(null);
    return false;
  }
}

export const api = {
  requestCode: (email: string) => call<void>('POST', '/auth/email/request', { email }),
  async verifyCode(email: string, code: string) {
    const t = await call<AuthTokens>('POST', '/auth/email/verify', { email, code, device: device() });
    setSession({ accessToken: t.accessToken, refreshToken: t.refreshToken, userId: t.userId });
  },
  /**
   * The app's one-time link: becomes this browser's session as the app's player (replacing any other).
   * A link that fails signs this browser out too: whoever was signed in here may not be the app's
   * player, and buying next would pay into their account.
   */
  async redeemHandoff(code: string) {
    const t = await call<AuthTokens>('POST', '/auth/web-handoff/redeem', { code, device: device() }, false).catch((err: unknown) => {
      setSession(null);
      throw err;
    });
    setSession({ accessToken: t.accessToken, refreshToken: t.refreshToken, userId: t.userId });
  },
  async signOut() {
    await call('POST', '/auth/logout').catch(() => undefined);
    setSession(null);
  },
  me: () => call<MeResponse>('GET', '/me'),
  character: () => call<CharacterView>('GET', '/characters/current'),
  wallet: () => call<WalletView>('GET', '/commerce/wallet'),
  catalog: (currency: WebCurrency | null) => call<WebCatalog>('GET', `/commerce/web/catalog${currency ? `?currency=${currency}` : ''}`),
  checkout: (productId: string, currency: WebCurrency, gift: boolean) => call<RedirectUrl>('POST', '/commerce/web/checkout', { productId, currency, gift }),
  portal: () => call<RedirectUrl>('POST', '/commerce/web/portal'),
  history: () => call<LedgerEntryView[]>('GET', '/commerce/web/history'),
  config: () => call<AppConfig>('GET', '/app/config', undefined, false),
  /** A chapter someone shared: public, no sign-in. */
  publicShare: (token: string) => call<PublicShareView>('GET', `/manga/shares/${encodeURIComponent(token)}`, undefined, false),
  /** Fire-and-forget: survives the page closing (a tap that leaves for the App Store). */
  shareEvent: (token: string, body: ShareEvent) =>
    void fetch(`${API_URL}/manga/shares/${encodeURIComponent(token)}/events`, { method: 'POST', keepalive: true, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).catch(() => undefined),
  legal: (page: LegalPage, l: string) => call<LegalDoc>('GET', `/legal/${page}/doc?lang=${encodeURIComponent(l)}`, undefined, false),
  prereg: (body: PreregRequest) => call<void>('POST', '/prereg', body, false),
};
